import { TenantId } from '@pelicargo/tenants';
import { useMemo } from 'react';

import { trpc } from '../config/trpc';

export type UseCustomerUrlsProps = {
  requestId?: number;
  quoteId?: number;
  quoteVariantId?: number;
  tenantId?: TenantId;
  fulfillmentPartnerConfigurationId?: number;
  isMarketplace?: boolean;
};

export const useCustomerUrls = ({
  requestId,
  quoteId,
  quoteVariantId,
  tenantId,
  fulfillmentPartnerConfigurationId,
  isMarketplace,
}: UseCustomerUrlsProps = {}) => {
  const { data: customerFrontendUrl } = trpc.getCustomerFrontendUrl.useQuery(tenantId ? { tenant_id: tenantId } : {}, {
    enabled: !!tenantId,
  });

  const isMarketplaceBooking = Boolean(isMarketplace || fulfillmentPartnerConfigurationId);

  const quoteListingPath = useMemo(() => {
    if (!requestId) return null;
    return `/requests/${requestId}`;
  }, [requestId]);

  const quotePath = useMemo(() => {
    if (!requestId || !quoteId) return null;
    return `/requests/${requestId}/quotes/${quoteId}`;
  }, [requestId, quoteId]);

  const quoteVariantPath = useMemo(() => {
    if (!requestId || !quoteId || !quoteVariantId) return null;
    return `/requests/${requestId}/quotes/${quoteId}/${quoteVariantId}`;
  }, [requestId, quoteId, quoteVariantId]);

  const bookingConfirmationPath = useMemo(() => {
    if (!requestId || !quoteId) return null;
    return `/requests/${requestId}/quotes/${quoteId}/booked`;
  }, [requestId, quoteId]);

  const marketplaceBookingPath = useMemo(() => {
    if (!requestId || !quoteId || !isMarketplaceBooking) return null;
    return `/marketplace/${requestId}/quotes/${quoteId}/booked`;
  }, [requestId, quoteId, isMarketplaceBooking]);

  const quoteListingUrl = useMemo(() => {
    if (!customerFrontendUrl || !quoteListingPath) return null;
    return `${customerFrontendUrl}${quoteListingPath}`;
  }, [customerFrontendUrl, quoteListingPath]);

  const quoteUrl = useMemo(() => {
    if (!customerFrontendUrl || !quotePath) return null;
    return `${customerFrontendUrl}${quotePath}`;
  }, [customerFrontendUrl, quotePath]);

  const quoteVariantUrl = useMemo(() => {
    if (!customerFrontendUrl || !quoteVariantPath) return null;
    return `${customerFrontendUrl}${quoteVariantPath}`;
  }, [customerFrontendUrl, quoteVariantPath]);

  const bookingConfirmationUrl = useMemo(() => {
    if (!customerFrontendUrl || !bookingConfirmationPath) return null;
    return `${customerFrontendUrl}${bookingConfirmationPath}`;
  }, [customerFrontendUrl, bookingConfirmationPath]);

  const marketplaceBookingUrl = useMemo(() => {
    if (!customerFrontendUrl || !marketplaceBookingPath) return null;
    return `${customerFrontendUrl}${marketplaceBookingPath}`;
  }, [customerFrontendUrl, marketplaceBookingPath]);

  return {
    customerFrontendUrl,
    quoteListingPath,
    quoteListingUrl,
    quotePath,
    quoteUrl,
    quoteVariantPath,
    quoteVariantUrl,
    bookingConfirmationPath,
    bookingConfirmationUrl,
    marketplaceBookingPath,
    marketplaceBookingUrl,
  };
};
